import React, { useState, useEffect } from "react";
import { useSendMessageMutation } from "../../services/loanApplicationApiSlice";
import { 
  FiX, 
  FiSend, 
  FiMessageSquare, 
  FiUser, 
  FiPhone, 
  FiCalendar, 
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle
} from "react-icons/fi";

const MessengerForm = ({ schedule, onClose }) => {
  const [message, setMessage] = useState("");
  const [sendMessage, { isLoading, isSuccess, isError, error }] = useSendMessageMutation();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('LKR', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Generate default message template
  const generateDefaultMessage = () => {
    // Use message amounts if provided (for Partially Paid or Overdue), otherwise use regular amounts
    const amountToShow = schedule.messageAmount !== undefined ? schedule.messageAmount : schedule.totalDue;
    const feestoShow = schedule.messageFeesDue !== undefined ? schedule.messageFeesDue : schedule.feesDue;
    const finalTotal = schedule.messageFinalTotal !== undefined ? schedule.messageFinalTotal : (schedule.totalDue + schedule.feesDue);
    
    return `Dear ${schedule.borrowerName},

This is a reminder that your installment No ${schedule.installmentNo} of ${formatCurrency(amountToShow)} is due on ${formatDate(schedule.dueDate)}.

Please ensure payment is made by the due date to avoid any late fees.

Installment Details:
- Installment Number: No ${schedule.installmentNo}
- Total Amount Due: ${formatCurrency(amountToShow)}
- Due Date: ${formatDate(schedule.dueDate)}
- Fees Due: ${formatCurrency(feestoShow)}
- Final Total : ${formatCurrency(finalTotal)}

Thank you for your timely payment.

Best regards,
Loan Management Team`;
  };

  // Initialize with default message
  useEffect(() => {
    setMessage(generateDefaultMessage());
  }, [schedule]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!schedule.phone) {
      alert("Error: Borrower phone number is not available.");
      return;
    }

    if (!message.trim()) {
      alert("Please enter a message to send.");
      return;
    }

    try {
      await sendMessage({
        phone: schedule.phone,
        message: message.trim()
      }).unwrap();
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  const handleTemplateInsert = (template) => {
    // Use message amounts if provided (for Partially Paid or Overdue), otherwise use regular amounts
    const amountToShow = schedule.messageAmount !== undefined ? schedule.messageAmount : schedule.totalDue;
    const feestoShow = schedule.messageFeesDue !== undefined ? schedule.messageFeesDue : schedule.feesDue;
    const finalTotal = schedule.messageFinalTotal !== undefined ? schedule.messageFinalTotal : (schedule.totalDue + schedule.feesDue);
    
    const templates = {
      reminder: `Dear ${schedule.borrowerName},

This is a friendly reminder that your installment No ${schedule.installmentNo} of ${formatCurrency(amountToShow)} is due on ${formatDate(schedule.dueDate)}.

Please ensure payment is made by the due date to avoid any late fees.

Installment Details:
- Installment Number: No ${schedule.installmentNo}
- Total Amount Due: ${formatCurrency(amountToShow)}
- Due Date: ${formatDate(schedule.dueDate)}
- Fees Due: ${formatCurrency(feestoShow)}
- Final Total : ${formatCurrency(finalTotal)}

Thank you for your timely payment.

Best regards,
Loan Management Team`,
      
      urgent: `URGENT: Payment Reminder for ${schedule.borrowerName}

Your installment No ${schedule.installmentNo} of ${formatCurrency(amountToShow)} is PAST DUE (Due: ${formatDate(schedule.dueDate)}).

Please make immediate payment to avoid additional penalties.

Installment Details:
- Installment Number: No ${schedule.installmentNo}
- Total Amount Due: ${formatCurrency(amountToShow)}
- Due Date: ${formatDate(schedule.dueDate)}
- Fees Due: ${formatCurrency(feestoShow)}
- Final Total: ${formatCurrency(finalTotal)}
Contact our office if you need assistance.

Best regards,
Loan Management Team`,
      
      thankYou: `Dear ${schedule.borrowerName},

Thank you for your continued prompt payments. 

This is a reminder that your next installment No ${schedule.installmentNo} of ${formatCurrency(amountToShow)} is due on ${formatDate(schedule.dueDate)}.

Please ensure payment is made by the due date to avoid any late fees.

Installment Details:
- Installment Number: No ${schedule.installmentNo}
- Total Amount Due: ${formatCurrency(amountToShow)}
- Due Date: ${formatDate(schedule.dueDate)}
- Fees Due: ${formatCurrency(feestoShow)}
- Final Total : ${formatCurrency(finalTotal)}
We appreciate your cooperation.

Best regards,
Loan Management Team`
    };
    
    setMessage(templates[template] || templates.reminder);
  };

  // Auto-close on success
  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess, onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-gray-200/50">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-white to-gray-50/90 backdrop-blur-sm border-b border-gray-200/50 rounded-t-2xl px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">
                Send Payment Reminder
              </h2>
              <p className="text-gray-600 mt-1 flex items-center gap-2">
                <FiMessageSquare className="w-4 h-4" />
                Send SMS message to borrower
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 group"
              aria-label="Close"
              disabled={isLoading}
            >
              <FiX className="w-5 h-5 text-gray-500 group-hover:text-gray-700" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Recipient Information */}
          <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-5 border border-gray-200/50 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiUser className="w-4 h-4" />
              Recipient Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <FiUser className="w-4 h-4 mr-2" />
                  <span className="text-sm">Borrower Name</span>
                </div>
                <p className="font-medium text-gray-900">{schedule.borrowerName || "N/A"}</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <FiPhone className="w-4 h-4 mr-2" />
                  <span className="text-sm">Phone Number</span>
                </div>
                <p className="font-medium text-gray-900">{schedule.phone || "N/A"}</p>
                {!schedule.phone && (
                  <p className="text-xs text-red-600 mt-1">Phone number is required to send message</p>
                )}
              </div>
            </div>
          </div>

          {/* Installment Details */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50/50 rounded-xl p-5 border border-blue-200/50 mb-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FiCalendar className="w-4 h-4" />
              Installment Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="text-gray-600 text-sm">Installment Number</div>
                <p className="font-bold text-blue-700">#{schedule.installmentNo}</p>
              </div>
              
              <div className="space-y-2">
                <div className="text-gray-600 text-sm">Due Date</div>
                <p className="font-medium text-gray-900">{formatDate(schedule.dueDate)}</p>
              </div>
              
              <div className="space-y-2">
                <div className="text-gray-600 text-sm">Total Amount Due</div>
                <p className="font-bold text-green-700">
                  {formatCurrency(schedule.messageAmount !== undefined ? schedule.messageAmount : schedule.totalDue)}
                </p>
                {schedule.messageAmount !== undefined && schedule.messageAmount !== schedule.totalDue && (
                  <p className="text-xs text-red-600">(Remaining: {formatCurrency(schedule.messageAmount)})</p>
                )}
              </div>
              
              <div className="space-y-2">
                <div className="text-gray-600 text-sm">Fees Due</div>
                <p className="font-medium text-gray-900">
                  {formatCurrency(schedule.messageFeesDue !== undefined ? schedule.messageFeesDue : schedule.feesDue)}
                </p>
                {schedule.messageFeesDue !== undefined && schedule.messageFeesDue !== schedule.feesDue && (
                  <p className="text-xs text-red-600">(Remaining: {formatCurrency(schedule.messageFeesDue)})</p>
                )}
              </div>
              
              <div className="space-y-2 md:col-span-2">
                <div className="text-gray-600 text-sm font-semibold">Final Total</div>
                <p className="font-bold text-blue-700 text-lg">
                  {formatCurrency(schedule.messageFinalTotal !== undefined ? schedule.messageFinalTotal : ((schedule.messageAmount !== undefined ? schedule.messageAmount : schedule.totalDue) + (schedule.messageFeesDue !== undefined ? schedule.messageFeesDue : schedule.feesDue)))}
                </p>
                {schedule.messageFinalTotal !== undefined && schedule.messageFinalTotal !== (schedule.totalDue + schedule.feesDue) && (
                  <p className="text-xs text-red-600">(Remaining: {formatCurrency(schedule.messageFinalTotal)})</p>
                )}
              </div>
            </div>
          </div>

          {/* Message Templates */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">
                Quick Templates
              </label>
              <span className="text-xs text-gray-500">Click to apply</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => handleTemplateInsert('reminder')}
                className="px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-200 text-left"
              >
                <div className="text-sm font-medium text-blue-700">Friendly Reminder</div>
                <div className="text-xs text-blue-600 mt-1">Standard payment reminder</div>
              </button>
              
              <button
                onClick={() => handleTemplateInsert('urgent')}
                className="px-4 py-3 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl hover:from-red-100 hover:to-red-200 transition-all duration-200 text-left"
              >
                <div className="text-sm font-medium text-red-700">Urgent Notice</div>
                <div className="text-xs text-red-600 mt-1">For overdue payments</div>
              </button>
              
              <button
                onClick={() => handleTemplateInsert('thankYou')}
                className="px-4 py-3 bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl hover:from-green-100 hover:to-green-200 transition-all duration-200 text-left"
              >
                <div className="text-sm font-medium text-green-700">Thank You & Reminder</div>
                <div className="text-xs text-green-600 mt-1">Appreciative tone</div>
              </button>
            </div>
          </div>

          {/* Message Form */}
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Message Content
                </label>
                <span className="text-xs text-gray-500">
                  {message.length} characters
                </span>
              </div>
              
              <div className="relative">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={10}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 focus:outline-none transition-all resize-none font-mono text-sm"
                  placeholder="Type your message here..."
                  required
                  disabled={isLoading}
                />
                <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                  SMS (160 chars per message)
                </div>
              </div>
              
              <div className="mt-3 text-sm text-gray-600">
                <p className="font-medium mb-1">Message will be sent to:</p>
                <p className="text-sm">{schedule.phone || "Phone number not available"}</p>
              </div>
            </div>

            {/* Status Messages */}
            {isSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <FiCheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-green-700 font-medium">
                    Message sent successfully!
                  </p>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  SMS has been sent to {schedule.borrowerName}.
                </p>
              </div>
            )}
            
            {isError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2">
                  <FiAlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-700 font-medium">
                    Failed to send message
                  </p>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  {error?.data?.message || "Please check the phone number and try again."}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                disabled={isLoading}
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={isLoading || !schedule.phone}
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend className="w-4 h-4" />
                    Send SMS Message
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Footer Note */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200/50 rounded-b-2xl px-6 py-4">
          <p className="text-xs text-gray-500 text-center">
            {isSuccess ? (
              <span className="text-green-600 font-medium">
                ✓ Message sent successfully! This window will close in 3 seconds...
              </span>
            ) : (
              `Messages are sent via SMS to ${schedule.phone || "the borrower's registered phone number"}`
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default MessengerForm;